import { useMsal } from '@azure/msal-react';
import {
    Button,
    Checkbox,
    Combobox,
    Dialog,
    DialogBody,
    DialogContent,
    DialogSurface,
    DialogTitle,
    Drawer,
    DrawerBody,
    DrawerHeader,
    DrawerHeaderTitle,
    Input,
    Label,
    makeStyles,
    Option,
    Radio,
    RadioGroup,
    Select,
    Spinner,
    Table,
    TableBody,
    TableCell,
    TableCellLayout,
    TableHeader,
    TableHeaderCell,
    TableRow,
    Textarea,
    tokens,
} from '@fluentui/react-components';
import { ArrowDownloadRegular, CheckmarkCircleRegular, DeleteRegular, DismissCircleRegular, Document24Regular, EditRegular, TableCalculatorRegular } from '@fluentui/react-icons';
import { FileTypeIcon } from '@fluentui/react-icons-file-type';
import { DatePicker } from '@fluentui/react-datepicker-compat';
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import type { ImportKalkulationRequest } from '../../models/ImportKalkulationRequest';
import type { KalkulationImportData } from '../../models/KalkulationImportData';
import type { KalkulationImportResult } from '../../models/KalkulationImportResult';
import type { ProjektDokumentItem } from '../../models/ProjektDokumentItem';
import type { ProjektItem } from '../../models/ProjektItem';
import type { ProjektmitarbeiterItem } from '../../models/ProjektmitarbeiterItem';
import { DataService } from '../../services/dataService';
import useConfigStore from '../../store/config';

const useStyles = makeStyles({
    container: {
        padding: tokens.spacingHorizontalL,
        height: '100%',
        overflow: 'auto',
        display: 'flex',
        flexDirection: 'column',
    },
    loadingContainer: {
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        padding: '40px 20px',
        minHeight: '300px',
    },
    emptyState: {
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        padding: '20px',
        minHeight: '100px',
        color: tokens.colorNeutralForeground3,
        fontSize: tokens.fontSizeBase300,
        backgroundColor: tokens.colorNeutralBackground2,
        borderRadius: tokens.borderRadiusMedium,
        textAlign: 'center',
        margin: '10px',
    },
    tableContainer: {
        display: 'flex',
        flexDirection: 'column',
        gap: tokens.spacingVerticalL,
        height: '100%',
        overflow: 'auto',
    },
    groupContainer: {
        marginBottom: tokens.spacingVerticalL,
    },
    groupHeader: {
        fontSize: tokens.fontSizeBase500,
        fontWeight: 700,
        color: tokens.colorBrandForeground1,
        paddingTop: tokens.spacingVerticalM,
        paddingBottom: tokens.spacingVerticalS,
        borderBottom: `2px solid ${tokens.colorNeutralStroke1}`,
    },
    tableRow: {
        cursor: 'pointer',
        '&:hover': {
            backgroundColor: tokens.colorNeutralBackground1Hover,
        },
    },
    drawerForm: {
        display: 'flex',
        flexDirection: 'column',
        gap: tokens.spacingVerticalL,
        padding: tokens.spacingVerticalL,
    },
    formGroup: {
        display: 'flex',
        flexDirection: 'column',
        gap: tokens.spacingVerticalS,
    },
    formLabel: {
        fontWeight: 700,
        fontSize: tokens.fontSizeBase400,
    },
    fileInput: {
        padding: tokens.spacingVerticalM,
        border: `1px solid ${tokens.colorNeutralStroke1}`,
        borderRadius: tokens.borderRadiusMedium,
        cursor: 'pointer',
    },
    largeTextarea: {
        width: '100%',
        fontSize: tokens.fontSizeBase300,
        minHeight: '120px',
        resize: 'none',
    },
    drawerFooter: {
        display: 'flex',
        gap: tokens.spacingHorizontalM,
        justifyContent: 'flex-end',
        padding: tokens.spacingVerticalL,
        borderTop: `1px solid ${tokens.colorNeutralStroke1}`,
    },
    colAction: {
        width: '100px',
        minWidth: '100px',
    },
    colDocument: {
        width: '40%',
        minWidth: '200px',
    },
    colDate: {
        width: '90px',
        minWidth: '90px',
    },
    colTime: {
        width: '70px',
        minWidth: '70px',
    },
    colStatus: {
        width: '100px',
        minWidth: '100px',
    },
    statusBadge: {
        padding: '4px 8px',
        borderRadius: tokens.borderRadiusMedium,
        fontSize: tokens.fontSizeBase300,
        fontWeight: 600,
        display: 'inline-block',
        width: 'fit-content',
    },
    statusApproved: {
        backgroundColor: tokens.colorStatusSuccessBackground2,
        color: tokens.colorStatusSuccessForeground1,
    },
    statusRejected: {
        backgroundColor: tokens.colorStatusDangerBackground2,
        color: tokens.colorStatusDangerForeground1,
    },
    documentCell: {
        display: 'flex',
        alignItems: 'center',
        gap: tokens.spacingHorizontalS,
    },
    documentIcon: {
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        flexShrink: 0,
        color: tokens.colorBrandForeground1,
    },
});

interface DokumenteListProps {
    project: ProjektItem | null;
}

// Mapping of dokTypID to group names
const DOC_TYPE_GROUPS: Record<number, string> = {
    1: 'Calculation',
    2: 'Proposal/Offer',
    3: 'Contract',
    4: 'Contract for Services',
    5: 'Confirmation',
    6: 'Final Report',
    7: 'Report',
    8: 'Refusal',
    9: 'Secrecy Agreement',
    10: 'Call Documents',
    11: 'Other',
    12: 'Partner Payment - Attachment',
    13: 'Partner Payment - Payment Proof',
    14: 'Invoices'
};

// Function to get icon based on file extension
const getFileIcon = (extension: string) => {
    const ext = extension.toLowerCase().replace(/^\./, '');

    // Use FileTypeIcon for most file types, fallback to Document24Regular for unknown types
    return ext ? <FileTypeIcon extension={ext} size={24} /> : <Document24Regular />;
};

const DokumenteListe: React.FC<DokumenteListProps> = ({ project }) => {
    const styles = useStyles();
    const { instance, accounts } = useMsal();
    const [documents, setDocuments] = useState<ProjektDokumentItem[]>([]);
    const [isLoading, setIsLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);
    const [isDrawerOpen, setIsDrawerOpen] = useState(false);
    const [selectedFile, setSelectedFile] = useState<File | null>(null);
    const [selectedDocType, setSelectedDocType] = useState<string>('1');
    const [uploadComment, setUploadComment] = useState<string>('');
    const [uploadDate, setUploadDate] = useState<Date | null>(null);
    const requiresUploadDate = selectedDocType === '5' || selectedDocType === '8';
    const [isUploading, setIsUploading] = useState(false);
    const [deleteConfirmationOpen, setDeleteConfirmationOpen] = useState(false);
    const [documentToDelete, setDocumentToDelete] = useState<ProjektDokumentItem | null>(null);
    const [isDeleting, setIsDeleting] = useState(false);
    const [editingDocument, setEditingDocument] = useState<ProjektDokumentItem | null>(null);
    const [isEditMode, setIsEditMode] = useState(false);
    const [approvalDrawerOpen, setApprovalDrawerOpen] = useState(false);
    const [documentForApproval, setDocumentForApproval] = useState<ProjektDokumentItem | null>(null);
    const [approvalType, setApprovalType] = useState<'approve' | 'reject' | null>(null);
    const [approvalComment, setApprovalComment] = useState<string>('');
    const [isProcessingApproval, setIsProcessingApproval] = useState(false);
    const [calculationDrawerOpen, setCalculationDrawerOpen] = useState(false);
    const [selectedCalculationDocument, setSelectedCalculationDocument] = useState<ProjektDokumentItem | null>(null);
    const [calculationData, setCalculationData] = useState<KalkulationImportData | null>(null);
    const [isLoadingCalculation, setIsLoadingCalculation] = useState(false);
    const [importSelection, setImportSelection] = useState<Record<number, boolean>>({});
    const [availableStaff, setAvailableStaff] = useState<ProjektmitarbeiterItem[]>([]);
    const [mitarbeiterSearchInputValues, setMitarbeiterSearchInputValues] = useState<Record<number, string>>({});
    const [mitarbeiterSearchResults, setMitarbeiterSearchResults] = useState<Record<number, ProjektmitarbeiterItem[]>>({});
    const [deleteExistingHours, setDeleteExistingHours] = useState<string>('yes');
    const [isImportingCalculation, setIsImportingCalculation] = useState(false);
    const [calculationImportResult, setCalculationImportResult] = useState<KalkulationImportResult | null>(null);
    const [showImportResult, setShowImportResult] = useState(false);
    const mitarbeiterSearchTimeoutRef = React.useRef<Record<number, ReturnType<typeof setTimeout> | null>>({});

    const { selectedRole } = useConfigStore((state) => state.roleConfig);
    const { userInfo: configUserInfo } = useConfigStore((state) => state.userConfig);
    const isAdmin = useMemo(() => selectedRole === 'Administratoren', [selectedRole]);
    const isControlling = useMemo(() => selectedRole === 'Controlling', [selectedRole]);

    // Fetch user info and documents on component mount or project change
    useEffect(() => {
        const fetchDocuments = async () => {
            if (!project) {
                setIsLoading(false);
                return;
            }

            try {
                setIsLoading(true);
                setError(null);

                const dataService = new DataService(instance, accounts[0]);
                const docs = await dataService.getProjektdokumentListe(project.projektID);
                setDocuments(docs as ProjektDokumentItem[]);
            } catch (err) {
                setError(err instanceof Error ? err.message : 'Error loading documents');
                console.error('Error fetching documents:', err);
            } finally {
                setIsLoading(false);
            }
        };

        fetchDocuments();
    }, [project, instance, accounts]);

    // Group documents by dokTypID
    const groupedDocuments = useMemo(() => {
        const groups: Record<number, ProjektDokumentItem[]> = {};

        documents.forEach((doc) => {
            if (!groups[doc.dokTypID]) {
                groups[doc.dokTypID] = [];
            }
            groups[doc.dokTypID].push(doc);
        });

        // Return only groups that have documents
        return Object.entries(groups)
            .filter(([typeId]) => DOC_TYPE_GROUPS[parseInt(typeId)])
            .sort((a, b) => parseInt(a[0]) - parseInt(b[0]));
    }, [documents]);

    // Handle double-click on document row
    const handleDocumentDoubleClick = async (doc: ProjektDokumentItem) => {
        try {
            const dataService = new DataService(instance, accounts[0]);
            const blob = await dataService.getProjektdokumentBinaries(doc.guid);
            // Create download link
            const url = window.URL.createObjectURL(blob);
            const link = document.createElement('a');
            link.href = url;
            link.download = doc.title; // Use the document's filename from the model
            document.body.appendChild(link);
            link.click();
            document.body.removeChild(link);
            window.URL.revokeObjectURL(url);
        } catch (err) {
            console.error('Error downloading document:', err);
            setError(`Error downloading document: ${err instanceof Error ? err.message : 'Unknown error'}`);
        }
    };

    // Handle edit document
    const handleEditClick = (doc: ProjektDokumentItem) => {
        setEditingDocument(doc);
        setSelectedDocType(doc.dokTypID.toString());
        setUploadComment(doc.uploadComment || '');
        setIsEditMode(true);
        setIsDrawerOpen(true);
    };

    // Handle delete document request
    const handleDeleteClick = (doc: ProjektDokumentItem) => {
        setDocumentToDelete(doc);
        setDeleteConfirmationOpen(true);
    };

    // Handle delete confirmation
    const handleConfirmDelete = async () => {
        if (!documentToDelete || !project) return;

        try {
            setIsDeleting(true);
            setError(null);
            const dataService = new DataService(instance, accounts[0]);
            await dataService.deleteProjektdokument(documentToDelete.dokumentID);

            // Refresh documents list
            const docs = await dataService.getProjektdokumentListe(project.projektID);
            setDocuments(docs as ProjektDokumentItem[]);
            setDeleteConfirmationOpen(false);
            setDocumentToDelete(null);
        } catch (err) {
            setError(`Error deleting document: ${err instanceof Error ? err.message : 'Unknown error'}`);
            console.error('Error deleting document:', err);
        } finally {
            setIsDeleting(false);
        }
    };

    // Handle cancel delete
    const handleCancelDelete = () => {
        setDeleteConfirmationOpen(false);
        setDocumentToDelete(null);
    };

    // Handle file upload
    const handleUploadDocument = async () => {
        if (!selectedFile || !project) {
            setError('Bitte wählen Sie eine Datei aus');
            return;
        }

        try {
            setIsUploading(true);
            setError(null);

            // Read file as base64
            const reader = new FileReader();
            reader.onload = async () => {
                try {
                    const base64Data = reader.result as string;
                    const base64String = base64Data.split(',')[1];

                    const dokumentData = {
                        projektID: project.projektID,
                        dokTypID: parseInt(selectedDocType),
                        dateiname: selectedFile.name,
                        extension: selectedFile.name.split('.').pop() || '',
                        uploadComment: uploadComment,
                        binaerDaten: base64String,
                    };

                    const dataService = new DataService(instance, accounts[0]);
                    await dataService.addProjektdokument(dokumentData, requiresUploadDate ? uploadDate ?? undefined : undefined);

                    // Reset form
                    setSelectedFile(null);
                    setSelectedDocType('1');
                    setUploadComment('');
                    setUploadDate(null);
                    setIsDrawerOpen(false);

                    // Refetch documents
                    const docs = await dataService.getProjektdokumentListe(project.projektID);
                    setDocuments(docs as ProjektDokumentItem[]);
                } catch (err) {
                    setError(`Error uploading document: ${err instanceof Error ? err.message : 'Unknown error'}`);
                    console.error('Error uploading document:', err);
                } finally {
                    setIsUploading(false);
                }
            };
            reader.onerror = () => {
                setError('Error reading the selected file');
                setIsUploading(false);
            };
            reader.readAsDataURL(selectedFile);
        } catch (err) {
            setIsUploading(false);
            setError(`Error uploading document: ${err instanceof Error ? err.message : 'Unknown error'}`);
        }
    };

    // Handle document update
    const handleUpdateDocument = async () => {
        if (!editingDocument || !project) {
            setError('Document information is missing');
            return;
        }

        try {
            setIsUploading(true);
            setError(null);

            const dokumentData = {
                ...editingDocument,
                dokTypID: parseInt(selectedDocType),
                uploadComment: uploadComment,
            };

            const dataService = new DataService(instance, accounts[0]);
            await dataService.updateProjektdokument(dokumentData);

            // Reset form
            setSelectedFile(null);
            setSelectedDocType('1');
            setUploadComment('');
            setEditingDocument(null);
            setIsEditMode(false);
            setIsDrawerOpen(false);

            // Refetch documents
            const docs = await dataService.getProjektdokumentListe(project.projektID);
            setDocuments(docs as ProjektDokumentItem[]);
        } catch (err) {
            setError(`Error updating document: ${err instanceof Error ? err.message : 'Unknown error'}`);
            console.error('Error updating document:', err);
        } finally {
            setIsUploading(false);
        }
    };

    // Handle drawer close
    const handleDrawerClose = () => {
        setIsDrawerOpen(false);
        setSelectedFile(null);
        setSelectedDocType('1');
        setUploadComment('');
        setUploadDate(null);
        setEditingDocument(null);
        setIsEditMode(false);
    };

    // Handle release button click (open approval drawer)
    const handleReleaseClick = (doc: ProjektDokumentItem) => {
        setDocumentForApproval(doc);
        setApprovalType(null);
        setApprovalComment('');
        setApprovalDrawerOpen(true);
    };

    // Handle approval or rejection submission
    const handleSubmitApproval = async () => {
        if (!documentForApproval || !approvalType || !approvalComment.trim() || !project) {
            setError('Please select approve/reject and enter a comment');
            return;
        }

        try {
            setIsProcessingApproval(true);
            setError(null);
            const dataService = new DataService(instance, accounts[0]);
            await dataService.freigabeInfoGroupCo(
                documentForApproval.dokumentID,
                approvalType === 'approve',
                approvalComment
            );
            // Refresh documents list
            const docs = await dataService.getProjektdokumentListe(project.projektID);
            setDocuments(docs as ProjektDokumentItem[]);
            // Close drawer
            setApprovalDrawerOpen(false);
            setDocumentForApproval(null);
            setApprovalType(null);
            setApprovalComment('');
        } catch (err) {
            setError(`Error processing approval: ${err instanceof Error ? err.message : 'Unknown error'}`);
            console.error('Error processing approval:', err);
        } finally {
            setIsProcessingApproval(false);
        }
    };

    // Handle approval drawer close
    const handleApprovalDrawerClose = () => {
        setApprovalDrawerOpen(false);
        setDocumentForApproval(null);
        setApprovalType(null);
        setApprovalComment('');
    };

    // Check if document can be released
    const canReleaseDocument = (doc: ProjektDokumentItem): boolean => {
        const isControllingRole = (doc.dokTypID === 1 || doc.dokTypID === 5) && isControlling;
        const isResponsibleMember = doc.dokTypID === 1 && configUserInfo?.userId === project?.zustaendigerMaControllingId;
        const isDepartmentLeader = doc.dokTypID === 2 && configUserInfo?.userId === project?.departmentLeiterID;
        return (isControllingRole || isResponsibleMember || isDepartmentLeader) && (doc.isApproved === undefined || !doc.isApproved) && (!doc.isRejected || doc.isRejected === undefined);
    };

    // Handle debounced search for mitarbeiter by index
    const handleMitarbeiterSearch = useCallback((searchTerm: string, index: number) => {
        if (mitarbeiterSearchTimeoutRef.current[index]) {
            clearTimeout(mitarbeiterSearchTimeoutRef.current[index]);
        }

        if (searchTerm.length < 2) {
            setMitarbeiterSearchResults((prev) => ({
                ...prev,
                [index]: [],
            }));
            return;
        }

        mitarbeiterSearchTimeoutRef.current[index] = setTimeout(() => {
            const filtered = availableStaff.filter((member) => {
                const fullName = member.fullName || `${member.vorname} ${member.nachname}`;
                return fullName.toLowerCase().includes(searchTerm.toLowerCase());
            });
            setMitarbeiterSearchResults((prev) => ({
                ...prev,
                [index]: filtered,
            }));
        }, 300);
    }, [availableStaff]);

    // Handle opening calculation drawer and loading data
    const handleOpenCalculation = async (doc: ProjektDokumentItem) => {
        try {
            setIsLoadingCalculation(true);
            setError(null);
            setSelectedCalculationDocument(doc);
            // Open drawer immediately to show loading state
            setCalculationDrawerOpen(true);

            const dataService = new DataService(instance, accounts[0]);
            const data = await dataService.getKalkulationDataFromXlsImport(doc.dokumentID);
            setCalculationData(data);
            console.log('Fetched calculation data:', data);

            // Load available staff
            const staff = await dataService.getMitarbeiter();
            setAvailableStaff(staff);

            // Initialize selection states
            if (data && data.mitarbeiterSollstunden && Array.isArray(data.mitarbeiterSollstunden)) {
                const initialImport: Record<number, boolean> = {};
                const initialSearchInputs: Record<number, string> = {};

                data.mitarbeiterSollstunden.forEach((person, index) => {
                    const personalId = person.personalID;
                    initialImport[index] = false;

                    if (personalId && staff) {
                        const foundStaff = staff.find((s) => s.personalID === personalId);
                        if (foundStaff) {
                            const fullName = foundStaff.fullName || `${foundStaff.vorname} ${foundStaff.nachname}`;
                            initialSearchInputs[index] = fullName;
                        } else {
                            initialSearchInputs[index] = '';
                        }
                    } else {
                        initialSearchInputs[index] = '';
                    }
                });

                setImportSelection(initialImport);
                setMitarbeiterSearchInputValues(initialSearchInputs);
            }
        } catch (err) {
            setError(`Error loading calculation data: ${err instanceof Error ? err.message : 'Unknown error'}`);
            console.error('Error loading calculation data:', err);
        } finally {
            setIsLoadingCalculation(false);
        }
    };

    // Handle closing calculation drawer
    const handleCloseCalculation = () => {
        setCalculationDrawerOpen(false);
        setSelectedCalculationDocument(null);
        setCalculationData(null);
        setImportSelection({});
        setAvailableStaff([]);
        setMitarbeiterSearchInputValues({});
        setMitarbeiterSearchResults({});
        setDeleteExistingHours('yes');
        setShowImportResult(false);
        setCalculationImportResult(null);
    };

    // Check if all selected rows have a mitarbeiter assigned
    const areAllSelectedRowsComplete = useMemo(() => {
        // Get all selected row indices
        const selectedIndices = Object.entries(importSelection)
            .filter(([, isSelected]) => isSelected)
            .map(([index]) => parseInt(index));

        // If no rows selected, return true (will be caught by other validation)
        if (selectedIndices.length === 0) return true;

        // Check if all selected rows have a mitarbeiter assigned
        return selectedIndices.every(
            (index) => mitarbeiterSearchInputValues[index] && mitarbeiterSearchInputValues[index].trim().length > 0
        );
    }, [importSelection, mitarbeiterSearchInputValues]);

    // Handle starting the import
    const handleStartImport = async () => {
        if (!project || !calculationData || !selectedCalculationDocument) {
            setError('Missing calculation data or project information');
            return;
        }

        // Validate that all selected rows have a mitarbeiter assigned
        if (!areAllSelectedRowsComplete) {
            setError('Please assign an employee for all selected rows before importing');
            return;
        }

        try {
            setIsImportingCalculation(true);
            setError(null);

            const dataService = new DataService(instance, accounts[0]);

            // Build the import request from selected data
            // For sollstunden, include only the selected rows with their personalized data
            const importedData = [];
            if (calculationData.mitarbeiterSollstunden) {
                for (let index = 0; index < calculationData.mitarbeiterSollstunden.length; index++) {
                    if (importSelection[index]) {
                        const person = calculationData.mitarbeiterSollstunden[index];
                        importedData.push(person);
                    }
                }
            }

            // Build the request
            const request = {
                dokumentId: selectedCalculationDocument.dokumentID,
                importedData: importedData,
                personalKalkulationString: '', // This might need to be populated if needed
                swsRedImportItem: calculationData.swsReduktion || {
                    swsImportError: false,
                    stundenReduzierung: 0,
                    neuanstellung: false,
                    swsRed: [],
                    hasFinanzdatenKonsortium: false,
                },
                alleSollStundenLoeschen: deleteExistingHours === 'yes',
            } as ImportKalkulationRequest;

            // Call the import API - always returns a result with status
            const result = await dataService.importKalkulation(request);
            setCalculationImportResult(result);
            setShowImportResult(true);
        } catch (err) {
            // Handle network or other critical errors
            setError(`Error importing calculation: ${err instanceof Error ? err.message : 'Unknown error'}`);
            console.error('Error importing calculation:', err);
        } finally {
            setIsImportingCalculation(false);
        }
    };

    // Render release status based on document approval status
    const renderReleaseStatus = (doc: ProjektDokumentItem) => {
        if (canReleaseDocument(doc)) {
            return (
                <Button
                    appearance="primary"
                    size="small"
                    onClick={() => handleReleaseClick(doc)}
                    title="Release document for approval"
                >
                    Approve
                </Button>
            );
        }

        if (doc.isApproved) {
            return (
                <div className={`${styles.statusBadge} ${styles.statusApproved}`}>
                    Approved
                </div>
            );
        }

        if (doc.isRejected) {
            return (
                <div className={`${styles.statusBadge} ${styles.statusRejected}`}>
                    Rejected
                </div>
            );
        }

        return null;
    };

    if (isLoading) {
        return (
            <div className={styles.container}>
                <div className={styles.loadingContainer}>
                    <Spinner label="Loading documents..." labelPosition="below" />
                </div>
            </div>
        );
    }

    if (error) {
        return (
            <div className={styles.container}>
                <div className={styles.emptyState} style={{ color: tokens.colorStatusDangerForeground1 }}>
                    {error}
                </div>
            </div>
        );
    }

    return (
        <>
            <div className={styles.container}>
                <div style={{ marginBottom: tokens.spacingVerticalM }}>
                    <Button appearance="primary" onClick={() => setIsDrawerOpen(true)}>
                        + Upload Document
                    </Button>
                </div>
                <div className={styles.tableContainer}>
                    {documents.length === 0 && (
                        <div className={styles.emptyState}>No documents available</div>
                    )}
                    {groupedDocuments.map(([typeId, docs]) => (
                        <div key={typeId} className={styles.groupContainer}>
                            <div className={styles.groupHeader}>
                                {DOC_TYPE_GROUPS[parseInt(typeId)]}
                            </div>
                            <Table
                                size="small"
                            >
                                <TableHeader>
                                    <TableRow>
                                        {isAdmin && <TableHeaderCell className={styles.colAction}>Action</TableHeaderCell>}
                                        <TableHeaderCell className={styles.colDocument}>Document</TableHeaderCell>
                                        <TableHeaderCell className={styles.colDate}>Date</TableHeaderCell>
                                        <TableHeaderCell className={styles.colTime}>Time</TableHeaderCell>
                                        <TableHeaderCell>Checked In By</TableHeaderCell>
                                        <TableHeaderCell>Check-In Comment</TableHeaderCell>
                                        <TableHeaderCell className={styles.colStatus}>Release Status</TableHeaderCell>
                                        <TableHeaderCell>Approved/Rejected By</TableHeaderCell>
                                        <TableHeaderCell>Approval Comment</TableHeaderCell>
                                    </TableRow>
                                </TableHeader>
                                <TableBody>
                                    {docs.map((doc) => (
                                        <TableRow
                                            key={doc.dokumentID}
                                            className={styles.tableRow}
                                            onDoubleClick={() => {
                                                console.log(doc);
                                                handleDocumentDoubleClick(doc);
                                            }}
                                        >
                                            {isAdmin && (
                                                <TableCell className={styles.colAction}>
                                                    <TableCellLayout>
                                                        <div style={{ display: 'flex', gap: tokens.spacingHorizontalXS }}>
                                                            <Button
                                                                icon={<EditRegular />}
                                                                appearance="subtle"
                                                                onClick={() => handleEditClick(doc)}
                                                                title="Dokument bearbeiten"
                                                            />
                                                            <Button
                                                                icon={<DeleteRegular />}
                                                                appearance="subtle"
                                                                onClick={() => handleDeleteClick(doc)}
                                                                title="Dokument löschen"
                                                            />
                                                            {doc.dokTypID === 1 && ['.xls', '.xlsx', '.xlsm'].includes(doc.extension?.toLowerCase()) && (
                                                                <Button
                                                                    icon={<TableCalculatorRegular />}
                                                                    appearance="subtle"
                                                                    onClick={() => handleOpenCalculation(doc)}
                                                                    title="View calculation data"
                                                                />
                                                            )}
                                                        </div>
                                                    </TableCellLayout>
                                                </TableCell>
                                            )}
                                            <TableCell className={styles.colDocument}>
                                                <TableCellLayout>
                                                    <div className={styles.documentCell}>
                                                        <div className={styles.documentIcon}>
                                                            {getFileIcon(doc.extension)}
                                                        </div>
                                                        <span>{doc.dateiname}</span>
                                                    </div>
                                                </TableCellLayout>
                                            </TableCell>
                                            <TableCell className={styles.colDate}>
                                                <TableCellLayout>
                                                    {doc.uploadedDate}
                                                </TableCellLayout>
                                            </TableCell>
                                            <TableCell className={styles.colTime}>
                                                <TableCellLayout>
                                                    {doc.uploadedTime}
                                                </TableCellLayout>
                                            </TableCell>
                                            <TableCell>
                                                <TableCellLayout>
                                                    {doc.uploadedByFullName}
                                                </TableCellLayout>
                                            </TableCell>
                                            <TableCell>
                                                <TableCellLayout>
                                                    {doc.uploadComment || '-'}
                                                </TableCellLayout>
                                            </TableCell>
                                            <TableCell className={styles.colStatus}>
                                                <TableCellLayout>
                                                    {renderReleaseStatus(doc)}
                                                </TableCellLayout>
                                            </TableCell>
                                            <TableCell>
                                                <TableCellLayout>
                                                    {doc.approvedByFullName || '-'}
                                                </TableCellLayout>
                                            </TableCell>
                                            <TableCell>
                                                <TableCellLayout>
                                                    {doc.approvalComment || '-'}
                                                </TableCellLayout>
                                            </TableCell>
                                        </TableRow>
                                    ))}
                                </TableBody>
                            </Table>
                        </div>
                    ))}
                </div>
            </div>

            {/* Delete Confirmation Dialog */}
            <Dialog
                open={deleteConfirmationOpen}
                onOpenChange={(_event, data) => {
                    if (!data.open) handleCancelDelete();
                }}
            >
                <DialogSurface>
                    <DialogContent>
                        <DialogTitle>Delete Document</DialogTitle>
                        <DialogBody>
                            <div style={{ marginBottom: tokens.spacingVerticalM }}>
                                Are you sure you want to delete the document "{documentToDelete?.dateiname}"?
                            </div>
                            <div style={{ marginBottom: tokens.spacingVerticalM, color: tokens.colorStatusWarningForeground1 }}>
                                This action cannot be undone.
                            </div>
                        </DialogBody>
                        <div style={{
                            display: 'flex',
                            gap: tokens.spacingHorizontalM,
                            justifyContent: 'flex-end',
                            padding: tokens.spacingVerticalL,
                            borderTop: `1px solid ${tokens.colorNeutralStroke1}`,
                        }}>
                            <Button
                                appearance="secondary"
                                onClick={handleCancelDelete}
                                disabled={isDeleting}
                            >
                                Cancel
                            </Button>
                            <Button
                                appearance="primary"
                                onClick={handleConfirmDelete}
                                disabled={isDeleting}
                            >
                                {isDeleting ? <Spinner size="small" /> : 'Delete'}
                            </Button>
                        </div>
                    </DialogContent>
                </DialogSurface>
            </Dialog>

            {/* Approval/Rejection Drawer */}
            <Drawer
                open={approvalDrawerOpen}
                onOpenChange={(_event, { open }) => {
                    if (!open) handleApprovalDrawerClose();
                }}
                position="end"
                size="medium"
            >
                <DrawerHeader>
                    <DrawerHeaderTitle>Approve/Reject Document</DrawerHeaderTitle>
                </DrawerHeader>
                <DrawerBody>
                    <div className={styles.drawerForm}>
                        {/* Document Info */}
                        {documentForApproval && (
                            <div className={styles.formGroup}>
                                <label className={styles.formLabel}>Document</label>
                                <div
                                    style={{
                                        padding: tokens.spacingVerticalM,
                                        backgroundColor: tokens.colorNeutralBackground2,
                                        borderRadius: tokens.borderRadiusMedium,
                                        display: 'flex',
                                        alignItems: 'center',
                                        gap: tokens.spacingHorizontalS,
                                    }}
                                >
                                    <div className={styles.documentCell}>
                                        <div className={styles.documentIcon}>
                                            {getFileIcon(documentForApproval.extension)}
                                        </div>
                                        <span>{documentForApproval.dateiname}</span>
                                    </div>
                                    <Button
                                        icon={<ArrowDownloadRegular />}
                                        appearance="subtle"
                                        onClick={() =>
                                            documentForApproval && handleDocumentDoubleClick(documentForApproval)
                                        }
                                        title="Download File"
                                        disabled={isProcessingApproval}
                                    />
                                </div>
                            </div>
                        )}

                        {/* Approval Type Selection */}
                        <div className={styles.formGroup}>
                            <Label required>Action</Label>
                            <div style={{ display: 'flex', gap: tokens.spacingHorizontalM }}>
                                <Button
                                    appearance="primary"
                                    icon={<CheckmarkCircleRegular />}
                                    onClick={() => setApprovalType('approve')}
                                    disabled={isProcessingApproval}
                                    style={{
                                        color: approvalType === 'approve' ? tokens.colorStatusSuccessForeground1 : undefined,
                                    }}
                                >
                                    Approve
                                </Button>
                                <Button
                                    appearance="primary"
                                    icon={<DismissCircleRegular />}
                                    onClick={() => setApprovalType('reject')}
                                    disabled={isProcessingApproval}
                                    style={{
                                        color: approvalType === 'reject' ? tokens.colorStatusDangerForeground1 : undefined,
                                    }}
                                >
                                    Reject
                                </Button>
                            </div>
                        </div>

                        {/* Comment Input */}
                        <div className={styles.formGroup}>
                            <Label htmlFor="approval-comment" required>
                                Comment
                            </Label>
                            <Textarea
                                id="approval-comment"
                                placeholder="Enter your approval/rejection comment..."
                                value={approvalComment}
                                onChange={(_e, data) => setApprovalComment(data.value)}
                                disabled={isProcessingApproval}
                                className={styles.largeTextarea}
                            />
                        </div>

                        {/* Error Message */}
                        {error && (
                            <p
                                style={{
                                    color: tokens.colorStatusDangerForeground1,
                                    fontSize: tokens.fontSizeBase300,
                                }}
                            >
                                {error}
                            </p>
                        )}
                    </div>
                </DrawerBody>

                {/* Approval Drawer Footer */}
                <div className={styles.drawerFooter}>
                    <Button
                        appearance="primary"
                        onClick={handleSubmitApproval}
                        disabled={
                            isProcessingApproval ||
                            !approvalType ||
                            !approvalComment.trim()
                        }
                    >
                        {isProcessingApproval ? (
                            <Spinner size="small" />
                        ) : approvalType === 'approve' ? (
                            'Approve'
                        ) : (
                            'Reject'
                        )}
                    </Button>
                    <Button
                        appearance="secondary"
                        onClick={handleApprovalDrawerClose}
                        disabled={isProcessingApproval}
                    >
                        Cancel
                    </Button>
                </div>
            </Drawer>

            {/* Upload/Edit Document Drawer */}
            <Drawer
                open={isDrawerOpen}
                onOpenChange={(_event, { open }) => {
                    if (!open) handleDrawerClose();
                }}
                position="end"
                size="medium"
            >
                <DrawerHeader>
                    <DrawerHeaderTitle>{isEditMode ? 'Edit Document' : 'Upload Document'}</DrawerHeaderTitle>
                </DrawerHeader>
                <DrawerBody>
                    <div className={styles.drawerForm}>
                        {/* File Input */}
                        <div className={styles.formGroup}>
                            <label className={styles.formLabel}>File</label>
                            {isEditMode ? (
                                <div style={{ padding: tokens.spacingVerticalM, backgroundColor: tokens.colorNeutralBackground2, borderRadius: tokens.borderRadiusMedium, display: 'flex', alignItems: 'center', gap: tokens.spacingHorizontalS }}>
                                    <p style={{ margin: 0, fontSize: tokens.fontSizeBase300, flex: 1 }}>
                                        {editingDocument?.dateiname}
                                    </p>
                                    <Button
                                        icon={<ArrowDownloadRegular />}
                                        appearance="subtle"
                                        onClick={() => editingDocument && handleDocumentDoubleClick(editingDocument)}
                                        title="Download File"
                                    />
                                </div>
                            ) : (
                                <>
                                    <input
                                        type="file"
                                        className={styles.fileInput}
                                        onChange={(e) => setSelectedFile(e.target.files?.[0] || null)}
                                        disabled={isUploading}
                                        title="Choose a file"
                                    />
                                    {selectedFile && (
                                        <p style={{ fontSize: tokens.fontSizeBase300, color: tokens.colorNeutralForeground2 }}>
                                            {selectedFile.name}
                                        </p>
                                    )}
                                </>
                            )}
                        </div>

                        {/* Document Type Select */}
                        <div className={styles.formGroup}>
                            <label className={styles.formLabel}>Document Type</label>
                            <Select
                                value={selectedDocType}
                                onChange={(_e, data) => setSelectedDocType(data.value)}
                                disabled={isUploading}
                            >
                                {Object.entries(DOC_TYPE_GROUPS).map(([typeId, name]) => (
                                    <option key={typeId} value={typeId}>
                                        {name}
                                    </option>
                                ))}
                            </Select>
                        </div>

                        {/* Date Input (required for Confirmation and Refusal) */}
                        {!isEditMode && requiresUploadDate && (
                            <div className={styles.formGroup}>
                                <label className={styles.formLabel}>Date *</label>
                                <DatePicker
                                    value={uploadDate}
                                    onSelectDate={(date) => setUploadDate(date ?? null)}
                                    placeholder="Select date"
                                    disabled={isUploading}
                                    formatDate={(date) => {
                                        if (!date) return '';
                                        const day = String(date.getDate()).padStart(2, '0');
                                        const month = String(date.getMonth() + 1).padStart(2, '0');
                                        return `${day}.${month}.${date.getFullYear()}`;
                                    }}
                                    parseDateFromString={(dateString) => {
                                        const parts = dateString.split('.');
                                        if (parts.length === 3) {
                                            return new Date(Number(parts[2]), Number(parts[1]) - 1, Number(parts[0]));
                                        }
                                        return null;
                                    }}
                                />
                            </div>
                        )}

                        {/* Comment Input */}
                        <div className={styles.formGroup}>
                            <label className={styles.formLabel}>Check-In Comment</label>
                            <Input
                                placeholder="Optional comment..."
                                value={uploadComment}
                                onChange={(_e, data) => setUploadComment(data.value)}
                                disabled={isUploading}
                            />
                        </div>

                        {/* Upload Progress */}
                        {isUploading && (
                            <div style={{ display: 'flex', alignItems: 'center', gap: tokens.spacingHorizontalS, color: tokens.colorNeutralForeground2 }}>
                                <Spinner size="tiny" />
                                <span style={{ fontSize: tokens.fontSizeBase300 }}>
                                    {isEditMode ? 'Updating document...' : 'Uploading document... Please wait.'}
                                </span>
                            </div>
                        )}

                        {/* Error Message */}
                        {error && (
                            <p style={{ color: tokens.colorStatusDangerForeground1, fontSize: tokens.fontSizeBase300 }}>
                                {error}
                            </p>
                        )}
                    </div>
                </DrawerBody>

                {/* Footer with Buttons */}
                <div className={styles.drawerFooter}>
                    <Button
                        appearance="primary"
                        onClick={isEditMode ? handleUpdateDocument : handleUploadDocument}
                        disabled={isUploading || (!isEditMode && (!selectedFile || (requiresUploadDate && !uploadDate)))}
                    >
                        {isUploading ? <Spinner size="small" /> : (isEditMode ? 'Update' : 'Upload')}
                    </Button>
                    <Button appearance="secondary" onClick={handleDrawerClose} disabled={isUploading}>
                        Cancel
                    </Button>
                </div>
            </Drawer>

            {/* Calculation Data Viewer Drawer */}
            <Drawer
                open={calculationDrawerOpen}
                onOpenChange={(_event, { open }) => {
                    if (!open) handleCloseCalculation();
                }}
                position="end"
                size="full"
            >
                <DrawerHeader>
                    <DrawerHeaderTitle>Read Calculation Data</DrawerHeaderTitle>
                </DrawerHeader>
                <DrawerBody>
                    {isLoadingCalculation ? (
                        <div className={styles.loadingContainer}>
                            <Spinner label="Loading calculation data..." labelPosition="below" />
                        </div>
                    ) : showImportResult && calculationImportResult ? (
                        <div className={styles.drawerForm}>
                            <div className={styles.formGroup}>
                                <label className={styles.formLabel}>Import Result</label>
                                <div style={{
                                    padding: tokens.spacingVerticalL,
                                    backgroundColor: calculationImportResult.isSuccessful ? tokens.colorStatusSuccessBackground2 : tokens.colorStatusDangerBackground2,
                                    borderRadius: tokens.borderRadiusMedium,
                                    border: `1px solid ${calculationImportResult.isSuccessful ? tokens.colorStatusSuccessForeground1 : tokens.colorStatusDangerForeground1}`,
                                    marginBottom: tokens.spacingVerticalL,
                                }}>
                                    <div style={{ display: 'flex', alignItems: 'center', gap: tokens.spacingHorizontalM, marginBottom: tokens.spacingVerticalM }}>
                                        {calculationImportResult.isSuccessful ? (
                                            <CheckmarkCircleRegular style={{ color: tokens.colorStatusSuccessForeground1, fontSize: '24px' }} />
                                        ) : (
                                            <DismissCircleRegular style={{ color: tokens.colorStatusDangerForeground1, fontSize: '24px' }} />
                                        )}
                                        <span style={{ fontSize: tokens.fontSizeBase400, fontWeight: 600, color: calculationImportResult.isSuccessful ? tokens.colorStatusSuccessForeground1 : tokens.colorStatusDangerForeground1 }}>
                                            {calculationImportResult.isSuccessful ? 'Import Successful' : 'Import Failed'}
                                        </span>
                                    </div>
                                    {calculationImportResult.errorMessage && (
                                        <p style={{ color: calculationImportResult.isSuccessful ? tokens.colorStatusSuccessForeground1 : tokens.colorStatusDangerForeground1, margin: 0, fontSize: tokens.fontSizeBase300 }}>
                                            {calculationImportResult.errorMessage}
                                        </p>
                                    )}
                                </div>
                            </div>

                            <div className={styles.formGroup}>
                                <label className={styles.formLabel}>Import Details</label>
                                <div style={{
                                    display: 'flex',
                                    flexDirection: 'column',
                                    gap: tokens.spacingVerticalM,
                                    padding: tokens.spacingVerticalM,
                                    backgroundColor: tokens.colorNeutralBackground2,
                                    borderRadius: tokens.borderRadiusMedium,
                                    border: `1px solid ${tokens.colorNeutralStroke1}`,
                                }}>
                                    <div style={{ display: 'flex', alignItems: 'center', gap: tokens.spacingHorizontalM }}>
                                        {calculationImportResult.sollStundenOk ? (
                                            <CheckmarkCircleRegular style={{ color: tokens.colorStatusSuccessForeground1, fontSize: '20px' }} />
                                        ) : (
                                            <DismissCircleRegular style={{ color: tokens.colorStatusDangerForeground1, fontSize: '20px' }} />
                                        )}
                                        <span style={{ fontWeight: calculationImportResult.sollStundenOk ? 'normal' : 600, color: calculationImportResult.sollStundenOk ? tokens.colorNeutralForeground1 : tokens.colorStatusDangerForeground1 }}>Working Hours</span>
                                    </div>

                                    <div style={{ display: 'flex', alignItems: 'center', gap: tokens.spacingHorizontalM }}>
                                        {calculationImportResult.finanzielleEckdatenOk ? (
                                            <CheckmarkCircleRegular style={{ color: tokens.colorStatusSuccessForeground1, fontSize: '20px' }} />
                                        ) : (
                                            <DismissCircleRegular style={{ color: tokens.colorStatusDangerForeground1, fontSize: '20px' }} />
                                        )}
                                        <span style={{ fontWeight: calculationImportResult.finanzielleEckdatenOk ? 'normal' : 600, color: calculationImportResult.finanzielleEckdatenOk ? tokens.colorNeutralForeground1 : tokens.colorStatusDangerForeground1 }}>Financial Data</span>
                                    </div>

                                    <div style={{ display: 'flex', alignItems: 'center', gap: tokens.spacingHorizontalM }}>
                                        {calculationImportResult.drittKostenOk ? (
                                            <CheckmarkCircleRegular style={{ color: tokens.colorStatusSuccessForeground1, fontSize: '20px' }} />
                                        ) : (
                                            <DismissCircleRegular style={{ color: tokens.colorStatusDangerForeground1, fontSize: '20px' }} />
                                        )}
                                        <span style={{ fontWeight: calculationImportResult.drittKostenOk ? 'normal' : 600, color: calculationImportResult.drittKostenOk ? tokens.colorNeutralForeground1 : tokens.colorStatusDangerForeground1 }}>Third-party Costs</span>
                                    </div>

                                    <div style={{ display: 'flex', alignItems: 'center', gap: tokens.spacingHorizontalM }}>
                                        {calculationImportResult.reiseKostenOk ? (
                                            <CheckmarkCircleRegular style={{ color: tokens.colorStatusSuccessForeground1, fontSize: '20px' }} />
                                        ) : (
                                            <DismissCircleRegular style={{ color: tokens.colorStatusDangerForeground1, fontSize: '20px' }} />
                                        )}
                                        <span style={{ fontWeight: calculationImportResult.reiseKostenOk ? 'normal' : 600, color: calculationImportResult.reiseKostenOk ? tokens.colorNeutralForeground1 : tokens.colorStatusDangerForeground1 }}>Travel Costs</span>
                                    </div>

                                    <div style={{ display: 'flex', alignItems: 'center', gap: tokens.spacingHorizontalM }}>
                                        {calculationImportResult.swsRedImportOk ? (
                                            <CheckmarkCircleRegular style={{ color: tokens.colorStatusSuccessForeground1, fontSize: '20px' }} />
                                        ) : (
                                            <DismissCircleRegular style={{ color: tokens.colorStatusDangerForeground1, fontSize: '20px' }} />
                                        )}
                                        <span style={{ fontWeight: calculationImportResult.swsRedImportOk ? 'normal' : 600, color: calculationImportResult.swsRedImportOk ? tokens.colorNeutralForeground1 : tokens.colorStatusDangerForeground1 }}>Hours Reduction Import</span>
                                    </div>
                                </div>
                            </div>
                        </div>
                    ) : calculationData ? (
                        <div className={styles.drawerForm}>
                            {/* Document Info */}
                            {selectedCalculationDocument && (
                                <div className={styles.formGroup}>
                                    <label className={styles.formLabel}>Document</label>
                                    <div
                                        style={{
                                            padding: tokens.spacingVerticalM,
                                            backgroundColor: tokens.colorNeutralBackground2,
                                            borderRadius: tokens.borderRadiusMedium,
                                            display: 'flex',
                                            alignItems: 'center',
                                            gap: tokens.spacingHorizontalS,
                                        }}
                                    >
                                        <div className={styles.documentCell}>
                                            <div className={styles.documentIcon}>
                                                {getFileIcon(selectedCalculationDocument.extension)}
                                            </div>
                                            <span>{selectedCalculationDocument.dateiname}</span>
                                        </div>
                                        <Button
                                            icon={<ArrowDownloadRegular />}
                                            appearance="subtle"
                                            onClick={() =>
                                                selectedCalculationDocument && handleDocumentDoubleClick(selectedCalculationDocument)
                                            }
                                            title="Download File"
                                        />
                                    </div>
                                </div>
                            )}

                            {/* Instructions */}
                            {calculationData && (
                                <div className={styles.formGroup}>
                                    <p style={{ fontSize: tokens.fontSizeBase300, color: tokens.colorNeutralForeground1, lineHeight: '1.5' }}>
                                        Please check for each person in the calculation whether an employee was found and thus correctly assigned in the "Employee" column.
                                        Employees can also be searched for and added by entering the name in the first field!<br />
                                        When importing the respective working hours, mark the data lines you want to import by clicking in the first column!
                                    </p>
                                </div>
                            )}

                            {/* Personnel Calculation Data Table */}
                            {calculationData && calculationData.mitarbeiterSollstunden && calculationData.mitarbeiterSollstunden.length > 0 ? (
                                <div className={styles.formGroup}>
                                    <label className={styles.formLabel}>Personnel Assigned Hours</label>
                                    <div style={{ overflowX: 'auto', borderRadius: tokens.borderRadiusMedium, border: `1px solid ${tokens.colorNeutralStroke1}` }}>
                                        <Table size="small">
                                            <TableHeader>
                                                <TableRow>
                                                    <TableHeaderCell style={{ width: '60px' }}>Import</TableHeaderCell>
                                                    <TableHeaderCell>Name (of Calculation)</TableHeaderCell>
                                                    <TableHeaderCell style={{ width: '200px' }}>Employee</TableHeaderCell>
                                                    {calculationData.semester && calculationData.semester.map((sem) => (
                                                        <TableHeaderCell key={sem}>{sem}</TableHeaderCell>
                                                    ))}
                                                </TableRow>
                                            </TableHeader>
                                            <TableBody>
                                                {calculationData.mitarbeiterSollstunden
                                                    .map((person, index) => (
                                                        <TableRow key={index}>
                                                            <TableCell>
                                                                <TableCellLayout>
                                                                    <Checkbox
                                                                        checked={importSelection[index] || false}
                                                                        onChange={(e) => setImportSelection({ ...importSelection, [index]: e.currentTarget.checked })}
                                                                    />
                                                                </TableCellLayout>
                                                            </TableCell>
                                                            <TableCell>
                                                                <TableCellLayout>
                                                                    {person.benutzername || '-'}
                                                                </TableCellLayout>
                                                            </TableCell>
                                                            <TableCell>
                                                                <TableCellLayout>
                                                                    <Combobox
                                                                        placeholder="Enter at least 2 characters..."
                                                                        value={mitarbeiterSearchInputValues[index] || ''}
                                                                        onOptionSelect={(_, option) => {
                                                                            const memberId = option.optionValue || '';
                                                                            const member = availableStaff.find((m) => m.personalID === parseInt(memberId));
                                                                            if (member) {
                                                                                const fullName = member.fullName || `${member.vorname} ${member.nachname}`;
                                                                                setMitarbeiterSearchInputValues((prev) => ({
                                                                                    ...prev,
                                                                                    [index]: fullName,
                                                                                }));
                                                                                // Also update the original calculationData
                                                                                setCalculationData((prev) => {
                                                                                    if (prev && prev.mitarbeiterSollstunden) {
                                                                                        const updated = { ...prev };
                                                                                        updated.mitarbeiterSollstunden = [...prev.mitarbeiterSollstunden];
                                                                                        updated.mitarbeiterSollstunden[index] = {
                                                                                            ...prev.mitarbeiterSollstunden[index],
                                                                                            personalID: member.personalID,
                                                                                        };
                                                                                        return updated;
                                                                                    }
                                                                                    return prev;
                                                                                });
                                                                            }
                                                                        }}
                                                                        onChange={(ev) => {
                                                                            const searchValue = (ev.target as HTMLInputElement).value;
                                                                            setMitarbeiterSearchInputValues((prev) => ({
                                                                                ...prev,
                                                                                [index]: searchValue,
                                                                            }));
                                                                            handleMitarbeiterSearch(searchValue, index);
                                                                        }}
                                                                        appearance="outline"
                                                                        size="small"
                                                                        style={{ minWidth: '150px' }}
                                                                    >
                                                                        {(mitarbeiterSearchResults[index] || []).map((member) => {
                                                                            const fullName = member.fullName || `${member.vorname} ${member.nachname}`;
                                                                            return (
                                                                                <Option
                                                                                    key={member.personalID}
                                                                                    value={member.personalID.toString()}
                                                                                    text={fullName}
                                                                                >
                                                                                    {fullName}
                                                                                </Option>
                                                                            );
                                                                        })}
                                                                    </Combobox>
                                                                </TableCellLayout>
                                                            </TableCell>
                                                            {calculationData.semester && calculationData.semester.map((sem, semIndex) => {
                                                                const sollStundenKey = `sollStundenSem${semIndex + 1}` as keyof typeof person;
                                                                const value = person[sollStundenKey];
                                                                return (
                                                                    <TableCell key={`${index}-${sem}`}>
                                                                        <TableCellLayout>
                                                                            {value ? String(value) : '0'}
                                                                        </TableCellLayout>
                                                                    </TableCell>
                                                                );
                                                            })}
                                                        </TableRow>
                                                    ))}
                                            </TableBody>
                                        </Table>
                                    </div>
                                </div>
                            ) : <div>No data available</div>}

                            {/* Delete Existing Hours RadioGroup */}
                            {calculationData && (
                                <div className={styles.formGroup}>
                                    <label className={styles.formLabel}>Should the existing hours be deleted?</label>
                                    <RadioGroup layout="horizontal" value={deleteExistingHours} onChange={(_, data) => setDeleteExistingHours(data.value)}>
                                        <Radio value="yes" label="Yes" />
                                        <Radio value="no" label="No" />
                                    </RadioGroup>
                                </div>
                            )}

                            {/* Data Import Status Checklist */}
                            {calculationData && (
                                <div className={styles.formGroup}>
                                    <label className={styles.formLabel}>Data available in calculation:</label>
                                    <div style={{ display: 'flex', flexDirection: 'column', gap: tokens.spacingVerticalM }}>
                                        {/* Financial data */}
                                        <div style={{ display: 'flex', alignItems: 'center', gap: tokens.spacingHorizontalM }}>
                                            {(calculationData.finanzielleEckdaten && Object.keys(calculationData.finanzielleEckdaten).length > 0) ? (
                                                <CheckmarkCircleRegular style={{ color: tokens.colorStatusSuccessForeground1, fontSize: '20px' }} />
                                            ) : (
                                                <DismissCircleRegular style={{ color: tokens.colorStatusWarningForeground1, fontSize: '20px' }} />
                                            )}
                                            <span>Financial data</span>
                                        </div>

                                        {/* Third-party costs */}
                                        <div style={{ display: 'flex', alignItems: 'center', gap: tokens.spacingHorizontalM }}>
                                            {(calculationData.drittKosten && calculationData.drittKosten.length > 0) ? (
                                                <CheckmarkCircleRegular style={{ color: tokens.colorStatusSuccessForeground1, fontSize: '20px' }} />
                                            ) : (
                                                <DismissCircleRegular style={{ color: tokens.colorStatusWarningForeground1, fontSize: '20px' }} />
                                            )}
                                            <span>Third-party costs</span>
                                        </div>

                                        {/* Travel costs */}
                                        <div style={{ display: 'flex', alignItems: 'center', gap: tokens.spacingHorizontalM }}>
                                            {(calculationData.reiseKosten && calculationData.reiseKosten.length > 0) ? (
                                                <CheckmarkCircleRegular style={{ color: tokens.colorStatusSuccessForeground1, fontSize: '20px' }} />
                                            ) : (
                                                <DismissCircleRegular style={{ color: tokens.colorStatusWarningForeground1, fontSize: '20px' }} />
                                            )}
                                            <span>Travel costs</span>
                                        </div>

                                        {/* Hours reduction in project */}
                                        <div style={{ display: 'flex', alignItems: 'center', gap: tokens.spacingHorizontalM }}>
                                            {calculationData.swsReduktion && calculationData.swsReduktion.stundenReduzierung ? (
                                                <CheckmarkCircleRegular style={{ color: tokens.colorStatusSuccessForeground1, fontSize: '20px' }} />
                                            ) : (
                                                <DismissCircleRegular style={{ color: tokens.colorStatusWarningForeground1, fontSize: '20px' }} />
                                            )}
                                            <span>Hours reduction in project</span>
                                        </div>

                                        {/* New hire in project */}
                                        <div style={{ display: 'flex', alignItems: 'center', gap: tokens.spacingHorizontalM }}>
                                            {calculationData.swsReduktion && calculationData.swsReduktion.neuanstellung ? (
                                                <CheckmarkCircleRegular style={{ color: tokens.colorStatusSuccessForeground1, fontSize: '20px' }} />
                                            ) : (
                                                <DismissCircleRegular style={{ color: tokens.colorStatusWarningForeground1, fontSize: '20px' }} />
                                            )}
                                            <span>New hire in project</span>
                                        </div>

                                        {/* Consortium financial data */}
                                        <div style={{ display: 'flex', alignItems: 'center', gap: tokens.spacingHorizontalM }}>
                                            {calculationData.swsReduktion && calculationData.swsReduktion.hasFinanzdatenKonsortium ? (
                                                <CheckmarkCircleRegular style={{ color: tokens.colorStatusSuccessForeground1, fontSize: '20px' }} />
                                            ) : (
                                                <DismissCircleRegular style={{ color: tokens.colorStatusWarningForeground1, fontSize: '20px' }} />
                                            )}
                                            <span>Consortium financial data</span>
                                        </div>
                                    </div>
                                </div>
                            )}

                            {/* Hours Reduction Table */}
                            {calculationData && calculationData.swsReduktion && calculationData.swsReduktion.swsRed && calculationData.swsReduktion.swsRed.length > 0 && (
                                <div className={styles.formGroup}>
                                    <label className={styles.formLabel}>Hours Reduction</label>
                                    <div style={{ overflowX: 'auto', borderRadius: tokens.borderRadiusMedium, border: `1px solid ${tokens.colorNeutralStroke1}` }}>
                                        <Table size="small">
                                            <TableHeader>
                                                <TableRow>
                                                    <TableHeaderCell>Name (of Calculation)</TableHeaderCell>
                                                    {calculationData.semester && calculationData.semester.map((sem) => (
                                                        <TableHeaderCell key={sem}>{sem}</TableHeaderCell>
                                                    ))}
                                                </TableRow>
                                            </TableHeader>
                                            <TableBody>
                                                {calculationData.swsReduktion.swsRed
                                                    .filter((person) => {
                                                        return !person.importedName || person.importedName !== 'nn';
                                                    })
                                                    .map((person, index) => (
                                                        <TableRow key={index}>
                                                            <TableCell>
                                                                <TableCellLayout>
                                                                    {person.importedName || person.fullName || '-'}
                                                                </TableCellLayout>
                                                            </TableCell>
                                                            {calculationData.semester && calculationData.semester.map((sem) => {
                                                                const value = person.stundenReduktion ? person.stundenReduktion[sem] : undefined;
                                                                return (
                                                                    <TableCell key={sem}>
                                                                        <TableCellLayout>
                                                                            {value !== undefined ? String(value) : '-'}
                                                                        </TableCellLayout>
                                                                    </TableCell>
                                                                );
                                                            })}
                                                        </TableRow>
                                                    ))}
                                            </TableBody>
                                        </Table>
                                    </div>
                                </div>
                            )}

                            {/* Error Message */}
                            {error && (
                                <p
                                    style={{
                                        color: tokens.colorStatusDangerForeground1,
                                        fontSize: tokens.fontSizeBase300,
                                    }}
                                >
                                    {error}
                                </p>
                            )}
                        </div>
                    ) : (
                        <div className={styles.emptyState}>
                            No calculation data available
                        </div>
                    )}
                </DrawerBody>

                {/* Calculation Drawer Footer */}
                <div className={styles.drawerFooter}>
                    {showImportResult ? (
                        <Button
                            appearance="secondary"
                            onClick={handleCloseCalculation}
                        >
                            Close
                        </Button>
                    ) : (
                        <>
                            <Button
                                appearance="primary"
                                onClick={handleStartImport}
                                disabled={isImportingCalculation || !Object.values(importSelection).some(v => v) || !areAllSelectedRowsComplete}
                            >
                                {isImportingCalculation ? <Spinner size="small" /> : 'Start Import'}
                            </Button>
                            <Button
                                appearance="secondary"
                                onClick={handleCloseCalculation}
                                disabled={isImportingCalculation}
                            >
                                Cancel
                            </Button>
                        </>
                    )}
                </div>
            </Drawer>
        </>
    );
};

export default DokumenteListe;
